import { useQuery } from '@tanstack/react-query'
import { api } from '@personic/shared'

interface AuditLogEntry {
  id: string
  actor_type: string
  actor_id: string
  actor_name: string
  action: string
  resource_type: string
  resource_id: string | null
  details: Record<string, any>
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

interface TenantActivityCardProps {
  tenantId: string
}

export default function TenantActivityCard({ tenantId }: TenantActivityCardProps) {
  const { data, isLoading, error } = useQuery<AuditLogEntry[]>({
    queryKey: ['tenant-activity', tenantId],
    queryFn: async () => {
      const res = await api.get(`/admin/audit-log?tenant_id=${tenantId}&limit=50`)
      return res.data
    },
  })

  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="text-gray-500">Loading activity...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6 bg-red-50 border-red-200">
        <p className="text-red-700">Failed to load activity log.</p>
      </div>
    )
  }

  const entries = data || []

  return (
    <div className="card">
      <div className="border-b border-gray-200 px-6 py-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Recent Activity ({entries.length})
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Last 50 audit log entries for this tenant
        </p>
      </div>

      {entries.length === 0 ? (
        <div className="px-6 py-8 text-center text-gray-500">
          No activity recorded for this tenant.
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {entries.map((entry) => (
            <div key={entry.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  <ActionIcon action={entry.action} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-gray-900">
                      {entry.actor_name}
                    </span>
                    <span className="text-sm text-gray-600">
                      {formatAction(entry.action)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatResourceType(entry.resource_type)}
                    </span>
                  </div>

                  {/* Details */}
                  {entry.details && Object.keys(entry.details).length > 0 && (
                    <div className="mt-1 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 font-mono">
                      {JSON.stringify(entry.details, null, 2)}
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                    <span>{new Date(entry.created_at).toLocaleString()}</span>
                    {entry.ip_address && (
                      <span className="font-mono">{entry.ip_address}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ActionIcon({ action }: { action: string }) {
  const icons: Record<string, string> = {
    create: '➕',
    update: '✏️',
    delete: '🗑️',
    login: '🔑',
    logout: '🚪',
    approve: '✅',
    reject: '❌',
    suspend: '⏸️',
    reactivate: '▶️',
  }

  const icon = icons[action] || '📝'

  return (
    <span className="text-xl" title={action}>
      {icon}
    </span>
  )
}

function formatAction(action: string): string {
  return action.replace(/_/g, ' ')
}

function formatResourceType(resourceType: string): string {
  return resourceType.replace(/_/g, ' ')
}
